import React, { useContext, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useSelector } from "react-redux";

import {
  useGetCustomerMeQuery,
  useUpdateCustomerMeMutation,
} from "../../../redux/services/customer";
import { useStartTelegramMutation } from "../../../redux/services/telegram";
import TelegramOTPModal from "../../../components/Auth/TelegramOTPModal";
import { ToastContext } from "../../../App";

const BOT_NICKNAME = process.env.TELEGRAM_BOT_NICKNAME || "DwtlLoginBot";

import UserIcon from "../../../assets/user.svg";
import NotificationIcon from "../../../assets/notification.svg";
import PencilIcon from "../../../assets/pencil.svg";

import * as styles from "./Account.module.css";

const FIELD_FIRST_NAME = "firstName";
const FIELD_LAST_NAME = "lastName";
const FIELD_PHONE = "phone";
const FIELD_EMAIL = "email";

const FIELD_LABELS = {
  [FIELD_FIRST_NAME]: "First Name",
  [FIELD_LAST_NAME]: "Last Name",
  [FIELD_PHONE]: "Phone Number",
  [FIELD_EMAIL]: "Email",
};

const Account = () => {
  const showToast = useContext(ToastContext);
  const reduxUser = useSelector((state) => state.user.user);

  const {
    data: profile,
    isLoading,
    refetch: refetchProfile,
  } = useGetCustomerMeQuery();
  const [updateCustomerMe, { isLoading: isSaving }] =
    useUpdateCustomerMeMutation();

  const [editingField, setEditingField] = useState(null);
  const [draftValue, setDraftValue] = useState("");
  const [error, setError] = useState(null);

  const merged = profile || reduxUser || {};

  const firstName = merged.first_name || "";
  const lastName = merged.last_name || "";
  const phone = merged.phone || "";
  const email = merged.email || "";

  // Telegram linking
  const [startTelegram] = useStartTelegramMutation();
  const [tlgStep, setTlgStep] = useState(null); // null | "otp"
  const [tlgToken, setTlgToken] = useState(null);
  const [tlgStartError, setTlgStartError] = useState(null);
  const [isTlgStarting, setIsTlgStarting] = useState(false);
  const [tlgUnlinked, setTlgUnlinked] = useState(false);

  const isTelegramLinked = !tlgUnlinked && !!merged.telegram_chat_id;

  const handleLinkTelegram = async () => {
    setTlgStartError(null);
    setIsTlgStarting(true);
    try {
      const data = await startTelegram().unwrap();
      const token = data?.start_token || data?.startToken || data?.id || null;
      const link = token
        ? `https://t.me/${BOT_NICKNAME}?start=${encodeURIComponent(token)}`
        : `https://t.me/${BOT_NICKNAME}`;
      window.open(link, "_blank", "noopener,noreferrer");
      setTlgToken(token);
      setTlgStep("otp");
    } catch (e) {
      setTlgStartError(
        e?.data?.message || e?.message || "Failed to start Telegram linking",
      );
    } finally {
      setIsTlgStarting(false);
    }
  };

  const handleTelegramSuccess = async (data) => {
    setTlgStep(null);
    setTlgToken(null);
    setTlgUnlinked(false);
    const chatId = data?.profile?.telegram_chat_id || data?.telegram_chat_id;
    if (chatId) {
      try {
        await updateCustomerMe({ telegram_chat_id: chatId }).unwrap();
        showToast?.(
          "success",
          "Telegram",
          "Telegram account linked successfully",
        );
      } catch (e) {
        showToast?.(
          "error",
          "Telegram",
          e?.data?.message || "Failed to save Telegram link",
        );
      }
    } else {
      showToast?.(
        "success",
        "Telegram",
        "Telegram account linked successfully",
      );
    }
  };

  const handleUnlinkTelegram = async () => {
    setTlgUnlinked(true);
    try {
      await updateCustomerMe({ telegram_chat_id: null }).unwrap();
      showToast?.("success", "Telegram", "Telegram account unlinked");
      refetchProfile();
    } catch (e) {
      setTlgUnlinked(false);
      showToast?.(
        "error",
        "Telegram",
        e?.data?.message || "Failed to unlink Telegram",
      );
    }
  };

  const startEdit = (field, currentValue) => {
    setEditingField(field);
    setDraftValue(currentValue || "");
    setError(null);
  };

  const cancelEdit = () => {
    setEditingField(null);
    setDraftValue("");
    setError(null);
  };

  const saveEdit = async () => {
    setError(null);
    const trimmed = draftValue.trim();
    const payload = {};

    if (editingField === FIELD_FIRST_NAME) {
      payload.first_name = trimmed || null;
    } else if (editingField === FIELD_LAST_NAME) {
      payload.last_name = trimmed || null;
    } else if (editingField === FIELD_PHONE) {
      if (!trimmed) {
        setError("Phone is required.");
        return;
      }
      payload.phone = trimmed;
    } else if (editingField === FIELD_EMAIL) {
      if (!trimmed) {
        setError("Email is required.");
        return;
      }
      payload.email = trimmed;
    } else {
      cancelEdit();
      return;
    }

    try {
      await updateCustomerMe(payload).unwrap();
      const fieldLabel = FIELD_LABELS[editingField] || "Profile";
      showToast?.("success", "Saved", `${fieldLabel} updated`);
      cancelEdit();
    } catch (e) {
      const msg =
        e?.data?.message ||
        (Array.isArray(e?.data?.errors) ? e.data.errors[0]?.message : null) ||
        e?.message ||
        "Failed to update profile";
      setError(msg);
    }
  };

  const renderField = (field, label, value, { editable = true } = {}) => {
    const isEditing = editingField === field;

    return (
      <div className={styles.field}>
        <div className={styles.fieldContent}>
          <div className={styles.fieldLabel}>{label}</div>
          {isEditing ? (
            <div className={styles.editInputRow}>
              <InputText
                className={styles.editInput}
                value={draftValue}
                onChange={(e) => setDraftValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveEdit();
                  if (e.key === "Escape") cancelEdit();
                }}
                autoFocus
              />
              <div className={styles.editActions}>
                <Button
                  type="button"
                  icon="pi pi-check"
                  rounded
                  text
                  severity="success"
                  onClick={saveEdit}
                  loading={isSaving}
                  aria-label="Save"
                />
                <Button
                  type="button"
                  icon="pi pi-times"
                  rounded
                  text
                  severity="secondary"
                  onClick={cancelEdit}
                  disabled={isSaving}
                  aria-label="Cancel"
                />
              </div>
            </div>
          ) : (
            <div className={styles.fieldValue}>{value || "—"}</div>
          )}
          {isEditing && error && (
            <div className={styles.errorText}>{error}</div>
          )}
        </div>
        {!isEditing && editable && (
          <button
            type="button"
            className={styles.editButton}
            onClick={() => startEdit(field, value)}
            aria-label={`Edit ${label}`}
          >
            <img src={PencilIcon} alt="" className={styles.editIcon} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <h1 className={styles.title}>My account</h1>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <img src={UserIcon} alt="" className={styles.cardHeaderIcon} />
          <span className={styles.cardHeaderTitle}>Personal Information</span>
        </div>
        <div className={styles.nameFieldsList}>
          {renderField(FIELD_FIRST_NAME, "First Name", firstName)}
          {renderField(FIELD_LAST_NAME, "Last Name", lastName)}
        </div>
      </div>

      <div className={styles.cardSpacer} />

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <img
            src={NotificationIcon}
            alt=""
            className={styles.cardHeaderIcon}
          />
          <span className={styles.cardHeaderTitle}>Notification Channels</span>
        </div>
        <div className={styles.channelsList}>
          <div className={styles.channelItem}>
            {renderField(FIELD_PHONE, "Phone Number", phone)}
          </div>
          <div className={styles.channelItem}>
            {renderField(FIELD_EMAIL, "Email", email)}
          </div>
          <div className={styles.channelItem}>
            <div className={styles.field}>
              <div className={styles.fieldContent}>
                <div className={styles.fieldLabel}>Telegram</div>
                <div className={styles.fieldValue}>
                  {isTelegramLinked
                    ? merged.telegram_username || merged.nickname
                      ? `@${merged.telegram_username || merged.nickname}`
                      : "Connected"
                    : "—"}
                </div>
                {tlgStartError && (
                  <div className={styles.errorText}>{tlgStartError}</div>
                )}
              </div>
              {isTelegramLinked ? (
                <button
                  type="button"
                  className={styles.editButton}
                  onClick={handleUnlinkTelegram}
                  disabled={isSaving}
                  aria-label="Unlink Telegram"
                >
                  <i
                    className="pi pi-times"
                    style={{ fontSize: 18, color: "rgba(77, 135, 190, 1)" }}
                  />
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.editButton}
                  onClick={handleLinkTelegram}
                  disabled={isTlgStarting}
                  aria-label="Link Telegram"
                >
                  <i
                    className="pi pi-link"
                    style={{ fontSize: 18, color: "rgba(77, 135, 190, 1)" }}
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {tlgStep === "otp" && tlgToken && (
        <TelegramOTPModal
          startToken={tlgToken}
          onUpdateStartToken={setTlgToken}
          startError={tlgStartError}
          onClose={() => {
            setTlgStep(null);
            setTlgToken(null);
          }}
          onSuccess={handleTelegramSuccess}
        />
      )}
    </div>
  );
};

export default Account;
